import {
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
    Combobox as HeadlessCombobox,
} from "@headlessui/react";
import clsx from "clsx";
import { Input } from "components/elements";
import { ChevronDownIcon } from "components/icons/solid";
import { XMarkIcon } from "components/icons/solid";
import { cn } from "lib/cn";
import { useRef, useState } from "react";

export const Combobox = <T extends string | number>({
    value,
    onChange,
    displayValue,
    options,
    className,
    inputClassName,
    placeholder,
    withClear,
}: {
    value: T | null | undefined;
    onChange: (value: T | null | undefined) => void;
    displayValue: (value: T | null | undefined) => string;
    options: (query: string) => { value: T; display: string }[] | null;
    className?: string;
    inputClassName?: string;
    placeholder?: string;
    withClear?: boolean;
}) => {
    const [query, setQuery] = useState("");
    const optionsRef = useRef<HTMLElement | null>(null);
    const filteredOptions = options(query);

    // Handle click events to prevent closing when scrolling
    const handleOptionsMouseDown = (e: React.MouseEvent) => {
        // Check if the click is on the scrollbar area
        const rect = optionsRef.current?.getBoundingClientRect();
        if (rect && optionsRef.current) {
            const scrollbarWidth = optionsRef.current.offsetWidth - optionsRef.current.clientWidth;
            const clickX = e.clientX - rect.left;
            // If clicking in the scrollbar area, prevent default
            if (clickX > rect.width - scrollbarWidth - 10) {
                e.preventDefault();
                e.stopPropagation();
            }
        }
    };

    return (
        // Headless UI 2: single comboboxes are always nullable, and the options panel
        // is positioned by its built-in anchor (it replaced our @floating-ui setup:
        // 4px gap, flips and shifts within 5px of the viewport, same width as the input).
        // The anchor writes an inline max-height, so the 15rem cap goes through
        // --anchor-max-height; a max-h-* class would be overridden.
        // `?? null` keeps it controlled: callers pass undefined for "no selection",
        // which React reports as switching between controlled and uncontrolled.
        <HeadlessCombobox value={value ?? null} onChange={onChange}>
            <div className={clsx("relative min-w-max flex-row", className)}>
                {/* Make the entire container clickable with cursor-pointer */}
                <ComboboxButton as="div" className="w-full cursor-pointer">
                    <div className="relative">
                        <ComboboxInput
                            onChange={(e) => setQuery(e.target.value)}
                            displayValue={displayValue}
                            className={cn(
                                inputClassName,
                                withClear
                                    ? "w-full pr-14 focus:ring-0"
                                    : "w-full pr-9 focus:ring-0",
                                "cursor-pointer", // Add cursor-pointer to input as well
                            )}
                            placeholder={placeholder}
                            autoComplete="off"
                            style={{ backgroundColor: "#f7f7f7" }}
                            as={Input}
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center px-2">
                            {/* This button is now just for visual, but clicking it will be handled by the parent button */}
                            <ChevronDownIcon className="h-6 w-6 p-1 text-slate-400" />
                            {withClear && (query || value) && (
                                <button
                                    type="button"
                                    // Headless UI 2's ComboboxButton toggles on pointerdown, not click,
                                    // so stopping the click alone no longer keeps the list closed.
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent the button click from opening the dropdown
                                        setQuery("");
                                        onChange(null);
                                    }}
                                    className="cursor-pointer" // Ensure clear button has cursor-pointer
                                >
                                    <XMarkIcon className="h-6 w-6 p-1 text-slate-400" />
                                </button>
                            )}
                        </div>
                    </div>
                </ComboboxButton>
            </div>
            {/* `anchor` portals the list to <body>. Inside a Radix modal Dialog, body has
                pointer-events: none (only the dialog content re-enables it), so the list
                needs pointer-events-auto or mouse clicks fall through and close it. */}
            <ComboboxOptions
                ref={optionsRef}
                anchor={{ to: "bottom start", gap: 4, padding: 5 }}
                className="pointer-events-auto z-99999 w-(--input-width) overflow-y-auto rounded-lg border border-slate-300 bg-[#def1fa] pl-0 shadow-lg [--anchor-max-height:15rem] focus:outline-hidden"
                style={{
                    scrollbarWidth: "thin",
                    scrollbarColor: "#cbd5e1 transparent",
                }}
                onMouseDown={handleOptionsMouseDown}
            >
                {!filteredOptions && (
                    <div className="px-2 py-2 text-sm text-gray-500">Loading...</div>
                )}
                {filteredOptions?.length === 0 && (
                    <div className="px-2 py-2 text-sm text-gray-500">No options found</div>
                )}
                {filteredOptions?.map((option) => (
                    <ComboboxOption
                        key={option.value}
                        value={option.value}
                        className={({ focus, selected }) =>
                            clsx(
                                "w-full cursor-pointer px-2 py-2 text-sm select-none",
                                focus && "bg-sky-200",
                                selected && "bg-sky-100 font-medium",
                                !focus && !selected && "hover:bg-sky-50",
                            )
                        }
                    >
                        {option.display}
                    </ComboboxOption>
                ))}
            </ComboboxOptions>
        </HeadlessCombobox>
    );
};
